import { errorProd as errorLog, success as successLog } from '@/utilities/log';

let globalErrorHandler: ((errorCode: number) => void) | null = null;

export const setGlobalErrorHandler = (handler: ((errorCode: number) => void) | null) => {
  globalErrorHandler = handler;
};

let socketIdGetter: (() => string | undefined) | null = null;

/**
 * Registered once Echo has a connection. Kept as a setter rather than an import so this
 * module stays free of a cycle with `lib/echo`, which already imports `damRequest`.
 */
export const setSocketIdGetter = (getter: (() => string | undefined) | null) => {
  socketIdGetter = getter;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
type RequestData = {
  headers?: Record<string, string>;
  /** Anything that can be serialized with JSON.stringify() */
  body?: any;
  /** When true, an unsuccessful response is not logged to the console (the caller handles it). */
  silent?: boolean;
};

export const getHostname = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return '';
};

/** Lets a second local stack run on a different port without stopping the first. */
export const getApiPort = () => Number(process.env.NEXT_PUBLIC_API_PORT) || 3001;

export const DAM_URL = process.env.NODE_ENV === 'development' ? `https://${getHostname()}:${getApiPort()}` : ``;

export const INTERNAL_V1 = '/api/v1/internal';
/**
 * Dam Request
 */
export const damRequest = (method: Method, uri: string, token?: string, data?: RequestData) =>
  request(DAM_URL, method, uri, token, data);

const request = async function (root_url: string, method: Method, uri: string, token?: string, data?: RequestData) {
  const { silent, ...fetchData } = data ?? {};
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(data?.headers ?? {}),
  };

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  // Laravel reads this header to resolve the calling socket, so an event broadcast during
  // this request can exclude it (`->toOthers()`). Without it a user receives an echo of
  // their own action and gets told their view changed by someone else.
  const socketId = socketIdGetter?.();
  if (socketId) {
    headers['X-Socket-Id'] = socketId;
  }

  let uriRequest = uri;

  if (process.env.NODE_ENV === 'development') {
    if (uri.includes('?')) {
      uriRequest += '&XDEBUG_SESSION=PHPSTORM';
    } else {
      uriRequest += '?XDEBUG_SESSION=PHPSTORM';
    }
  }

  try {
    const response = await fetch(`${root_url}${uriRequest}`, {
      method,
      ...fetchData,
      headers,
      ...(data?.body && { body: JSON.stringify(data.body) }),
    });

    const contentType = response.headers.get('content-type');
    const responseData = contentType?.includes('application/json') ? await response.json() : null;

    if (!response.ok) {
      if (response.status === 401 && globalErrorHandler) {
        globalErrorHandler(401);
      }

      let errorPayload: Record<string, unknown> & { message?: string; status: number };

      if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
        errorPayload = { ...(responseData as Record<string, unknown>), status: response.status };
        if (typeof errorPayload.message !== 'string' && typeof errorPayload.error === 'string') {
          errorPayload.message = errorPayload.error;
        }
      } else {
        // Add status code to non-json errors
        errorPayload = {
          message: responseData ? String(responseData) : response.statusText,
          status: response.status,
        };
      }

      throw errorPayload;
    }

    successLog('API Response', uri, {
      request: {
        uri,
        data,
      },
      data: responseData,
      response,
    });

    return responseData ?? response.statusText;
  } catch (error: any) {
    if (!silent) {
      errorLog('API Error', error);
    }
    throw error;
  }
};
